import React, { Suspense, ReactNode } from 'react';
import { RouteObject } from 'react-router-dom';
import { lazy } from 'react';

// Lazy‑loaded page components
const Home = lazy(() => import('../pages/home/page'));
const About = lazy(() => import('../pages/about/page'));
const CaseStudies = lazy(() => import('../pages/case-studies/page'));
const Blog = lazy(() => import('../pages/blog/page'));
const BlogArticle = lazy(() => import('../pages/blog-article/page'));
const PrivacyPolicy = lazy(() => import('../pages/privacy-policy/page'));
const TermsOfService = lazy(() => import('../pages/terms-of-service/page'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Service pages
const Services = lazy(() => import('../pages/services/page'));
const SocialMediaManagement = lazy(() => import('../pages/services/social-media-management/page'));
const VideoProduction = lazy(() => import('../pages/services/video-production/page'));
const InfluencerMarketing = lazy(() => import('../pages/services/influencer-marketing/page'));
const PaidAdvertising = lazy(() => import('../pages/services/paid-advertising/page'));
const ContentStrategy = lazy(() => import('../pages/services/content-strategy/page'));
const AnalyticsReporting = lazy(() => import('../pages/services/analytics-reporting/page'));

const Assessment = lazy(() => import('../pages/assessment/page'));

// Helper to wrap a lazy component with a fallback UI
const withSuspense = (element: ReactNode): ReactNode => (
  <Suspense fallback={<div>Loading…</div>}>{element}</Suspense>
);

import ServicesPage from "../pages/services/page";
import ContactPage from "../pages/contact/page";
import InfluencerMarketingPage from "../pages/services/influencer-marketing/page";
import BrandingPage from "../pages/services/branding/page";
import WebDesign from "../pages/services/web-design/page";
import WebDesignThankYou from "../pages/services/web-design/thank-you/page";

const BrandingThankYouPage = lazy(() => import("../pages/services/branding/thank-you/page"));

const routes: RouteObject[] = [
  {
    path: '/',
    element: withSuspense(<Home />),
  },
  {
    path: '/about',
    element: withSuspense(<About />),
  },
  {
    path: '/services',
    element: <ServicesPage />,
  },
  {
    path: '/case-studies',
    element: withSuspense(<CaseStudies />),
  },
  {
    path: '/blog',
    element: withSuspense(<Blog />),
  },
  {
    path: '/blog/:slug',
    element: withSuspense(<BlogArticle />),
  },
  {
    path: '/privacy-policy',
    element: withSuspense(<PrivacyPolicy />),
  },
  {
    path: '/terms-of-service',
    element: withSuspense(<TermsOfService />),
  },
  {
    path: '/services/social-media-management',
    element: withSuspense(<SocialMediaManagement />),
  },
  {
    path: '/services/video-production',
    element: withSuspense(<VideoProduction />),
  },
  {
    path: '/services/influencer-marketing',
    element: <InfluencerMarketingPage />,
  },
  {
    path: '/services/branding',
    element: <BrandingPage />,
  },
  {
    path: '/services/branding/thank-you',
    element: <BrandingThankYouPage />,
  },
  {
    path: '/services/web-design',
    element: <WebDesign />,
  },
  {
    path: '/services/web-design/thank-you',
    element: <WebDesignThankYou />,
  },
  {
    path: '/services/paid-advertising',
    element: withSuspense(<PaidAdvertising />),
  },
  {
    path: '/services/content-strategy',
    element: withSuspense(<ContentStrategy />),
  },
  {
    path: '/services/analytics-reporting',
    element: withSuspense(<AnalyticsReporting />),
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/assessment',
    element: withSuspense(<Assessment />),
  },
  {
    path: '/admin',
    lazy: async () => {
      const { default: AdminPage } = await import('../pages/admin/page');
      return { Component: AdminPage };
    },
  },
  {
    path: '*',
    element: withSuspense(<NotFound />),
  },
];

export default routes;
